import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, LinearProgress, Snackbar, useTheme } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Section from '../../../../components/Main/Section/Section';
import Guide from '../../../../components/Guide/Guide';
import GroupWizard from '../../../../components/GroupWizard/GroupWizard';
import { IMonitoring } from '../../../../shared/interfaces/IMonitoring';
import ReviewMonitoringService from '../../../../services/ReviewMonitoringService';
import GroupService from '../../../../services/GroupService';
import { CurrentViewContext } from '../../../../providers/CurrentViewProvider';
import MonitoringNoContent from '../../../../assets/images/monitoring_no_content.png';

function getFaqLink() {
  return process.env.REACT_APP_FAQ_LINK ?? '';
}

const GroupsGuide = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [monitoringEntries, setMonitoringEntries] = useState<IMonitoring[]>([]);
  const [hasGroups, setHasGroups] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);
  const [showSavedNotice, setShowSavedNotice] = useState(false);

  const handleCloseSavedNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setShowSavedNotice(false);
  };

  useEffect(() => {
    setCurrentView(t('nav_groups'));
    document.title = `${t('app_branding')} ${t('nav_groups')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    const controller = new AbortController();

    const loadMonitoring = async () => {
      const data = await ReviewMonitoringService.getMonitoring(
        true,
        'both',
        setLoading,
        controller.signal
      );
      if (!data || 'success' in data) return;
      setMonitoringEntries(data as IMonitoring[]);
    };

    const loadGroups = async () => {
      const groups = await GroupService.getGroups();
      setHasGroups(groups.length > 0);
    };

    (async () => {
      await loadMonitoring();
      await loadGroups();
    })();

    return () => controller.abort();
  }, []);

  const handleSubmit = async (
    data: {
      name: string;
      color: string;
      monitoringEntryIds: string[];
    },
    options?: { createAnother?: boolean }
  ) => {
    try {
      await GroupService.createGroup(data);
      setHasGroups(true);
      if (!options?.createAnother) {
        navigate(-1);
      } else {
        setShowSavedNotice(true);
      }
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const handleAnotherGroup = () => {
    setWizardKey((k) => k + 1);
  };

  return (
    <Section>
      {loading && (
        <Box>
          <LinearProgress sx={{ my: theme.spacing(1) }} />
        </Box>
      )}
      <Guide
        image={
          <img
            src={MonitoringNoContent}
            width={300}
            style={{ marginBottom: hasGroups ? theme.spacing(6) : 0 }}
            alt="Groups Guide"
          />
        }
      >
        <Box sx={{ maxWidth: 600 }}>
          <GroupWizard
            key={wizardKey}
            monitoringEntries={monitoringEntries}
            onSubmit={handleSubmit}
            onCancel={() => navigate(-1)}
            onAnotherGroup={handleAnotherGroup}
          />
        </Box>
      </Guide>
      <Snackbar
        open={showSavedNotice}
        autoHideDuration={3000}
        onClose={handleCloseSavedNotice}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSavedNotice}
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('group_saved_continue')}
        </Alert>
      </Snackbar>
    </Section>
  );
};

export default GroupsGuide;
